import { useState, useEffect } from 'react';
import axios from 'axios';

import { getCreateCartPayload } from '../modules/authentication/utils/common';
import useApiStatus from '../store/apiStatus/useApiStatus';
import { status } from '../helpers/constants';
import * as urls from '../helpers/urlConst';

axios.defaults.baseURL = urls.CHECKOUT_API_URL;
axios.defaults.headers = { Accept: 'application/json' };
axios.defaults.withCredentials = false;

export const clientAxios = axios;

export const useAxios = () => {
  const [apiResponse, setApiResponse] = useState(undefined);
  const [apiError, setApiError] = useState('');
  const [apiLoading, setApiLoading] = useState(false);

  const apiFetchData = async params => {
    try {
      setApiLoading(true);
      const result = await axios.request(params);
      setApiResponse(result.data);
    } catch (err) {
      setApiError(err.response);
    } finally {
      setApiLoading(false);
    }
  };

  const apiReset = () => {
    setApiResponse(undefined);
    setApiError('');
    setApiLoading(false);
  };

  return { apiResponse, apiError, apiLoading, apiFetchData, apiReset };
};

export const useMerchantCreateCartAPI = () => {
  const { apiResponse, apiError, apiLoading, apiFetchData } = useAxios();

  const apiMerchantCreateCart = merchantId => {
    apiFetchData({
      method: 'POST',
      url: `/merchants/${merchantId}/create_cart`,
      data: getCreateCartPayload(),
    });
  };

  return { apiResponse, apiError, apiLoading, apiMerchantCreateCart };
};

export const useCartDetailsAPI = () => {
  const { apiResponse, apiError, apiLoading, apiFetchData, apiReset } = useAxios();

  const apiCartDetails = (cartId, deviceId) => {
    apiFetchData({
      method: 'POST',
      url: `/carts/${cartId}/details`,
      data: { deviceId },
    });
  };

  return { apiResponse, apiError, apiLoading, apiCartDetails, apiReset };
};

export const useEmailPostApi = () => {
  const { apiResponse, apiError, apiLoading, apiFetchData } = useAxios();
  const { set: setApiStatus } = useApiStatus();

  const apiPostEmail = ({ cartId, deviceId }, email) => {
    apiFetchData({
      method: 'POST',
      url: `/carts/${cartId}/with_email`,
      data: { deviceId, email },
    });
  };

  useEffect(() => {
    if (apiResponse) setApiStatus({ isEmailReady: status.READY });
  }, [apiResponse]);
  useEffect(() => {
    if (apiError) setApiStatus({ isEmailReady: status.ERROR });
  }, [apiError]);
  useEffect(() => {
    if (apiLoading) setApiStatus({ isEmailReady: status.LOADING });
  }, [apiLoading]);

  return { apiResponse, apiError, apiLoading, apiPostEmail };
};

export const useAnalyticsMethodApi = () => {
  const { apiResponse, apiError, apiLoading, apiFetchData } = useAxios();

  const apiPostAnalyticsMethod = (cartId, api) => {
    apiFetchData({
      method: 'POST',
      url: `/analytics/carts/${cartId}/${api}`,
    });
  };

  return { apiResponse, apiError, apiLoading, apiPostAnalyticsMethod };
};

export const useSubmitOrderApi = () => {
  const { apiResponse, apiError, apiLoading, apiFetchData } = useAxios();

  const apiPostOrder = ({
    cartId,
    cardType,
    deviceId,
    email,
    shippingName,
    contextShippingAddress,
    shipCompany,
    shippingMethodId,
    userCardId,
    userCardCvv,
    contextBillingAddress,
    billingPhone,
    cvvChanged,
    isUserSaveInfo,
    newsletter,
    orderNotes,
    consent,
    applyStoreCredit,
    payerId,
    paymentId,
    method,
    amazonCheckoutSessionId,
    giftCertificates,
    type,
    paymentData,
    socialImpact,
    pickUpMethodKey,
  }) => {
    const data = {
      deviceId,
      email,
      shipName: shippingName,
      shipAddress: contextShippingAddress,
      shipCompany,
      shipMethodKey: shippingMethodId,
      cardId: userCardId,
      cardType,
      saveUserInfo: isUserSaveInfo,
      giftCertificates,
    };
    if (type === 'submit') {
      data.payment = {
        additionalInformation: {
          cardCvv: userCardCvv,
          hasCardCvvChanged: cvvChanged,
        },
        id: userCardId,
        type: 'direct',
      };
    }
    if (billingPhone?.indexOf('+') > -1) {
      data.billPhone = billingPhone.replace(/\s/g, '');
    } else if (billingPhone) {
      data.billPhone = `+${billingPhone.replace(/\s/g, '')}`;
    }

    if (Object.keys(contextBillingAddress).length > 0) {
      data.billName = contextBillingAddress.fullName;
      data.billAddress = contextBillingAddress;
    }
    if (newsletter) {
      data.plugins = {
        newsletter: {
          consent,
        },
      };
    }
    if (orderNotes) {
      data.orderNotes = orderNotes;
    }
    if (payerId) {
      data.payerId = payerId;
    }
    if (paymentId) {
      data.paymentId = paymentId;
    }
    if (amazonCheckoutSessionId) {
      data.amazonCheckoutSessionId = amazonCheckoutSessionId;
    }
    if (applyStoreCredit) {
      data.applyStoreCredit = applyStoreCredit;
    }
    if (paymentData) {
      data.payment = {
        ...paymentData,
      };
    }
    if (socialImpact) {
      data.socialImpact = {
        ...socialImpact,
        email,
      };
    }
    if (pickUpMethodKey) {
      data.pickUpMethodKey = pickUpMethodKey;
    }

    data.method = method || 'credit-card';

    apiFetchData({
      method: 'POST',
      url: `/carts/${cartId}/submit`,
      data,
    });
  };

  return { apiResponse, apiError, apiLoading, apiPostOrder };
};
